import { InjectionToken, Provider } from '@angular/core';

// Returns a type that allows all keys of T, and requires all keys of T not included in U
// Example: T = {foo: any, bar: any}; U = {foo: 1}; would return {foo?: any, bar: any}
export type Completion<T, U extends Partial<T>> = Partial<T> & Omit<T, keyof U>;
export type TokenCompletion<
  T,
  U extends Partial<V>,
  V = T extends InjectionToken<infer R> ? R : never,
> = Completion<V, U>;

// Returns type-safe value provider that only requires required keys that have no default value
export function ProviderWithDefaults<T, U extends Partial<T>, V extends Completion<T, U>>(
  token: InjectionToken<T>,
  defaults: U,
  ...providers: Provider[]
  // If V is empty, then options is an optional argument
): (
  ...args: {} extends V
    ? [options?: V | ((...cbArgs: any[]) => V), deps?: any[]]
    : [options: V | ((...cbArgs: any[]) => V), deps?: any[]]
) => Provider[] {
  return (options?: V | ((...cbArgs: typeof deps) => V), deps: any[] = []) => [
    ...providers,
    {
      provide: token,
      useFactory: (...args: typeof deps) => {
        const overrides = typeof options === 'function' ? options(...args) : options;
        return { ...defaults, ...overrides };
      },
      deps,
    },
  ];
}
